import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import SectionLabel from '@/components/generals/section-label'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'
import { diagnosis } from '@/constants/content'

// "You've evolved. Your website hasn't." told as a scrolled diagnosis: the
// symptoms are logged one by one, then the verdict is struck through and
// rewritten, and every symptom is signed off as fixed.
const Diagnosis: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = sectionRef.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.desktop, () => {
            gsap.set(q('[data-strike]'), { scaleX: 0 })
            gsap.set(q('[data-will]'), { clipPath: 'inset(0% 100% 0% 0%)' })
            gsap.set(q('[data-issue-strike]'), { scaleX: 0 })
            gsap.set(q('[data-tag]'), { yPercent: 0 })

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 0.7
                }
            })

            timeline
                .fromTo(q('[data-issue]'), {
                    autoAlpha: 0,
                    xPercent: 12,
                    clipPath: 'inset(0% 0% 0% 100%)'
                }, {
                    autoAlpha: 1,
                    xPercent: 0,
                    clipPath: 'inset(0% 0% 0% 0%)',
                    stagger: 0.12,
                    duration: 0.16,
                    ease: 'power2.out'
                }, 0.04)
                .to(q('[data-strike]'), { scaleX: 1, duration: 0.12, ease: 'power2.inOut' }, 0.6)
                .to(q('[data-hasnt]'), { opacity: 0.32, duration: 0.08 }, 0.66)
                .to(q('[data-will]'), { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.14, ease: 'power2.out' }, 0.68)
                .to(q('[data-issue-strike]'), { scaleX: 1, stagger: 0.03, duration: 0.08, ease: 'power2.inOut' }, 0.82)
                .to(q('[data-tag]'), { yPercent: -50, stagger: 0.03, duration: 0.08, ease: 'power2.inOut' }, 0.84)
                .to({}, { duration: 0.05 })
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    return (
        <section
            id='diagnosis'
            ref={sectionRef}
            data-scene='blue'
            aria-labelledby='diagnosis-title'
            className='relative motion-safe:lg:h-[320vh]'
        >
            <div className='flex flex-col justify-center px-5 py-28 md:px-8 lg:h-svh lg:py-0 motion-safe:lg:sticky motion-safe:lg:top-0'>
                <SectionLabel index='02'>
                    Diagnosis
                </SectionLabel>

                <div className='mt-10 grid gap-14 lg:mt-14 lg:grid-cols-12 lg:gap-10'>
                    <h2 id='diagnosis-title' className='type-h1 lg:col-span-7'>
                        <span className='sr-only'>
                            {"You've evolved. Your website hasn't. With us, it will."}
                        </span>

                        <span aria-hidden='true' className='block'>
                            {"You've evolved."}
                        </span>

                        <span aria-hidden='true' className='block text-quiet'>
                            Your website
                        </span>

                        <span aria-hidden='true' className='flex flex-wrap items-baseline gap-x-[0.28em]'>
                            <span className='relative text-quiet'>
                                <span data-hasnt=''>
                                    {"hasn't."}
                                </span>

                                <span data-strike='' className='absolute inset-x-[-0.04em] top-[52%] h-[0.08em] origin-left bg-mark' />
                            </span>

                            <span data-will='' className='type-serif text-mark'>
                                will.
                            </span>
                        </span>
                    </h2>

                    <div className='lg:col-span-5'>
                        <p className='type-lead text-quiet'>
                            {diagnosis.intro}
                        </p>

                        <ol className='mt-10 border-t border-rule'>
                            {diagnosis.issues.map((issue, index) => (
                                <li key={issue.title} data-issue='' className='grid grid-cols-[2.5rem_1fr_auto] gap-x-4 border-b border-rule py-5'>
                                    <span className='type-label pt-1 text-quiet'>
                                        0{index + 1}
                                    </span>

                                    <div>
                                        <h3 className='relative inline-block text-lg font-semibold tracking-[-0.015em]'>
                                            {issue.title}

                                            <span data-issue-strike='' aria-hidden='true' className='absolute inset-x-0 top-1/2 h-px origin-left scale-x-0 bg-current motion-reduce:scale-x-0' />
                                        </h3>

                                        <p className='mt-1 text-quiet'>
                                            {issue.text}
                                        </p>
                                    </div>

                                    <span aria-hidden='true' className='type-label block h-[1.4em] overflow-hidden pt-1'>
                                        <span data-tag='' className='flex flex-col'>
                                            <span className='h-[1.4em]'>
                                                Issue
                                            </span>

                                            <span className='h-[1.4em] text-mark'>
                                                Fixed
                                            </span>
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Diagnosis
